import PropTypes from "prop-types";
import React, {useEffect, useState} from "react";
import {
    Avatar,
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Grid,
    IconButton,
    MenuItem,
    Select,
    Typography,
} from "@mui/material";
import SoftButton from "../../../components/SoftButton";
import PhotoCamera from "@mui/icons-material/PhotoCamera";
import {extractDOBFromMyKad} from "../../../utils/myKadDOBExtractor";
import SoftInput from "../../../components/SoftInput";
import SoftTypography from "../../../components/SoftTypography";
import SoftBox from "../../../components/SoftBox";
import {getAllBranches} from "../../../api/branch/branchApiClient";
import {
    formatMyKad,
    validateAge,
    validateBranch,
    validateEmail,
    validateE164Phone,
    validateMyKad,
    validatePassport,
    validateName,
    validateRequired
} from "../../../utils/formValidations";
import {handleApiError, showCreateSuccess, showFormErrors, showUpdateSuccess} from "../../../utils/notificationUtils";

function AddGuardianDialog({open, onClose, onSubmit, guardian, isEdit}) {
    let initialState = {
        firstName: "",
        lastName: "",
        email: "",
        phoneNumber: "",
        userRole: "GUARDIAN",
        gender: "",
        status: "ACTIVE",
        profilePic: null,
        idType: "",
        idNumber: "",
        dob: "",
        branchId: ""
    };
    const [formData, setFormData] = useState(initialState);
    const [error, setError] = useState(null);
    const [previewUrl, setPreviewUrl] = useState(null);
    const [errors, setErrors] = useState({});
    const [branches, setBranches] = useState([]);
    const [loadingBranches, setLoadingBranches] = useState(false);
    const [branchesError, setBranchesError] = useState(null);

    // Load branches from backend
    const loadBranches = async () => {
        setLoadingBranches(true);
        setBranchesError(null);
        try {
            const response = await getAllBranches();
            if (response.data && Array.isArray(response.data)) {
                setBranches(response.data);
                response.data.forEach(b => console.log(b.branchId))
            } else {
                setBranchesError('Invalid branch data received');
            }
        } catch (error) {
            console.error('Error loading branches:', error);
            setBranchesError(error.message || 'Failed to load branches');
        } finally {
            setLoadingBranches(false);
        }
    };

    useEffect(() => {
        if (open) {
            loadBranches();
        }
    }, [open]);

    useEffect(() => {
        if (isEdit && guardian) {
            console.log('Edit mode - Guardian data:', guardian);
            console.log('Birth date from backend:', guardian.birthDate);

            const formattedDob = guardian.birthDate ? guardian.birthDate.split('T')[0] : "";
            console.log('Formatted DOB for form:', formattedDob);
            
            setFormData({
                firstName: guardian.firstName || "",
                lastName: guardian.lastName || "",
                email: guardian.email || "",
                // Strip "+" from phone number for display (user only types digits)
                phoneNumber: guardian.phoneNumber ? guardian.phoneNumber.replace(/^\+/, '') : "",
                userRole: guardian.userRole || "GUARDIAN",
                gender: guardian.gender || "",
                status: guardian.status || "ACTIVE",
                profilePic: null,
                idType: guardian.idType || "",
                idNumber: guardian.idNumber || "",
                dob: formattedDob,
                branchId: guardian.branchId || ""
            });
            if (guardian.profilePic) {
                setPreviewUrl(`data:image/png;base64,${guardian.profilePic}`);
            } else {
                setPreviewUrl(null);
            }
        } else if (open) {
            setFormData(initialState);
            setPreviewUrl(null);
        }
    }, [isEdit, guardian, open]);

    const handleChange = (e) => {
        const {name, value} = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleImageChange = (e) => {
        const file = e.target.files[0];
        if (validateImage(e)) {
            if (file) {
                setFormData(prev => ({
                    ...prev,
                    profilePic: file
                }));
                const reader = new FileReader();
                reader.onloadend = () => {
                    setPreviewUrl(reader.result);
                };
                reader.readAsDataURL(file);
            }
        } else {
            setFormData(prev => ({
                ...prev,
                profilePic: null
            }));
        }
    };

    function clearData() {
        setFormData(initialState);
        setPreviewUrl(null);
        setError(null);
        setErrors({});
    }

    const validateImage = (e) => {
        const file = e.target.files?.[0];

        if (!file) {
            setError('No file selected');
            setPreviewUrl(null);
            return false;
        }

        if (file.size > 5 * 1024 * 1024) {
            setError('Image size should be less than 5MB');
            setPreviewUrl(null);
            e.target.value = '';
            return false;
        }

        if (!file.type.startsWith('image/')) {
            setError('Please upload a valid image file');
            setPreviewUrl(null);
            e.target.value = '';
            return false;
        }

        setError('');
        return true;
    };

    // Validation function
    const validateForm = () => {
        const newErrors = {};

        // Required field validations
        const firstNameError = validateName(formData.firstName, 'First name');
        if (firstNameError) newErrors.firstName = firstNameError;

        const lastNameError = validateName(formData.lastName, 'Last name');
        if (lastNameError) newErrors.lastName = lastNameError;

        const emailError = validateEmail(formData.email);
        if (emailError) newErrors.email = emailError;

        // Prepend "+" before validation (user only types digits)
        const phoneNumberWithPrefix = formData.phoneNumber ? '+' + formData.phoneNumber : '';
        const phoneError = validateE164Phone(phoneNumberWithPrefix);
        if (phoneError) newErrors.phoneNumber = phoneError;

        const branchError = validateBranch(formData.branchId);
        if (branchError) newErrors.branchId = branchError;

        // ID number validation
        if (formData.idType === 'MYKAD') {
            const myKadError = validateMyKad(formData.idNumber);
            if (myKadError) newErrors.idNumber = myKadError;
        } else if (formData.idType === 'PASSPORT') {
            const passportError = validatePassport(formData.idNumber);
            if (passportError) newErrors.idNumber = passportError;
        } else {
            const idError = validateRequired(formData.idNumber, 'ID number');
            if (idError) newErrors.idNumber = idError;
        }

        // Profile picture validation (required)
        //if (!formData.profilePic && !previewUrl) {
        //    newErrors.profilePic = 'Profile picture is required';
        //}

        // Date of birth validation (must be 18+ for guardians)
        if (formData.dob) {
            const ageError = validateAge(formData.dob, 18); // Guardians must be 18+
            if (ageError) newErrors.dob = ageError;
        } else {
            newErrors.dob = 'Date of birth is required';
        }

        // Gender validation (required)
        if (!formData.gender) {
            newErrors.gender = 'Gender is required';
        }

        return newErrors;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        // Clear previous errors
        setErrors({});
        setError(null);

        // Validate form
        const validationErrors = validateForm();

        if (Object.keys(validationErrors).length > 0) {
            setErrors(validationErrors);
            showFormErrors(validationErrors);
            return;
        }

        try {
            const formDataToSend = new FormData();
            Object.keys(formData).forEach(key => {
                if (key === 'profilePic') {
                    if (formData.profilePic) {
                        formDataToSend.append(key, formData[key]);
                    }
                } else {
                    let value = formData[key];
                    // Prepend "+" to phone number before submitting
                    if (key === 'phoneNumber' && typeof value === 'string' && value) {
                        value = '+' + value;
                    }
                    // Always send email in lowercase
                    if (key === 'email' && typeof value === 'string') {
                        value = value.toLowerCase();
                    }
                    formDataToSend.append(key, value);
                }
            });

            // Submit form
            await onSubmit(formDataToSend);

            // Show success message
            if (isEdit) {
                showUpdateSuccess('Guardian');
            } else {
                showCreateSuccess('Guardian');
            }
            
            clearData();
            onClose();
        } catch (error) {
            handleApiError(error, isEdit ? "Failed to update guardian" : "Failed to create guardian");
        }
    };

    function handleIdChange(e) {
        handleChange(e);

        // Auto-extract date of birth from MyKad
        if (e.target.name === 'idNumber' && formData.idType === 'MYKAD') {
            const newBirthDate = extractDOBFromMyKad(e.target.value);
            if (newBirthDate) {
                setFormData(prev => ({
                    ...prev,
                    dob: new Date(newBirthDate).toISOString().split("T")[0]
                }));
            }
        }

        // When ID type changes to MyKad and there's already an ID number
        if (e.target.name === 'idType' && e.target.value === 'MYKAD' && formData.idNumber) {
            const newBirthDate = extractDOBFromMyKad(formData.idNumber);
            if (newBirthDate) {
                setFormData(prev => ({
                    ...prev,
                    dob: new Date(newBirthDate).toISOString().split("T")[0]
                }));
            }
        }

        // Clear DOB when ID type changes away from MyKad
        if (e.target.name === 'idType' && e.target.value !== 'MYKAD') {
            setFormData(prev => ({
                ...prev,
                dob: ''
            }));
        }
    }

    return (
        <>
            <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
                <DialogTitle>{isEdit ? "Edit Guardian" : "Add New Guardian"}</DialogTitle>
                <form onSubmit={handleSubmit}>
                    <DialogContent>
                        <Grid container spacing={2}>
                            <Grid item xs={12} sx={{display: 'flex', justifyContent: 'center', mb: 2}}>
                                <Box sx={{position: 'relative'}}>
                                    <Avatar
                                        src={previewUrl}
                                        sx={{width: 100, height: 100, mb: 1}}
                                    />
                                    <input
                                        accept="image/*"
                                        style={{display: 'none'}}
                                        id="profile-pic-upload"
                                        type="file"
                                        onChange={handleImageChange}
                                    />
                                    <label htmlFor="profile-pic-upload">
                                        <IconButton
                                            color="primary"
                                            component="span"
                                            sx={{
                                                position: 'absolute',
                                                bottom: 0,
                                                right: 0,
                                                backgroundColor: 'white',
                                                '&:hover': {
                                                    backgroundColor: 'grey.100'
                                                }
                                            }}
                                        >
                                            <PhotoCamera/>
                                        </IconButton>
                                    </label>
                                </Box>
                            </Grid>

                            <Grid item xs={12} sm={6}>
                                <SoftBox mb={0}>
                                    <SoftBox mb={1} ml={0.5}>
                                        <SoftTypography
                                            component="label"
                                            variant="caption"
                                            fontWeight="bold"
                                        >
                                            First Name *
                                        </SoftTypography>
                                    </SoftBox>
                                    <SoftInput
                                        name="firstName"
                                        type="text"
                                        placeholder="Enter first name"
                                        value={formData.firstName}
                                        onChange={handleChange}
                                        error={!!errors.firstName}
                                        sx={{
                                            '& .MuiOutlinedInput-root': {
                                                '&.Mui-error .MuiOutlinedInput-notchedOutline': {
                                                    borderColor: '#d32f2f',
                                                },
                                                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                                    borderColor: errors.firstName ? '#d32f2f' : '#1976d2',
                                                },
                                            },
                                        }}
                                        required
                                    />
                                    {errors.firstName && (
                                        <SoftTypography
                                            variant="caption"
                                            color="error"
                                            sx={{mt: 0.5, ml: 1}}
                                        >
                                            {errors.firstName}
                                        </SoftTypography>
                                    )}
                                </SoftBox>
                            </Grid>

                            <Grid item xs={12} sm={6}>
                                <SoftBox mb={0}>
                                    <SoftBox mb={1} ml={0.5}>
                                        <SoftTypography
                                            component="label"
                                            variant="caption"
                                            fontWeight="bold"
                                        >
                                            Last Name *
                                        </SoftTypography>
                                    </SoftBox>
                                    <SoftInput
                                        name="lastName"
                                        type="text"
                                        placeholder="Enter last name"
                                        value={formData.lastName}
                                        onChange={handleChange}
                                        error={!!errors.lastName}
                                        sx={{
                                            '& .MuiOutlinedInput-root': {
                                                '&.Mui-error .MuiOutlinedInput-notchedOutline': {
                                                    borderColor: '#d32f2f',
                                                },
                                                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                                    borderColor: errors.lastName ? '#d32f2f' : '#1976d2',
                                                },
                                            },
                                        }}
                                        required
                                    />
                                    {errors.lastName && (
                                        <SoftTypography
                                            variant="caption"
                                            color="error"
                                            sx={{mt: 0.5, ml: 1}}
                                        >
                                            {errors.lastName}
                                        </SoftTypography>
                                    )}
                                </SoftBox>
                            </Grid>

                            <Grid item xs={12}>
                                <SoftBox mb={0}>
                                    <SoftBox mb={1} ml={0.5}>
                                        <SoftTypography
                                            component="label"
                                            variant="caption"
                                            fontWeight="bold"
                                        >
                                            Email *
                                        </SoftTypography>
                                    </SoftBox>
                                    <SoftInput
                                        name="email"
                                        type="email"
                                        placeholder="Enter email address"
                                        value={formData.email}
                                        onChange={handleChange}
                                        error={!!errors.email}
                                        sx={{
                                            '& .MuiOutlinedInput-root': {
                                                '&.Mui-error .MuiOutlinedInput-notchedOutline': {
                                                    borderColor: '#d32f2f',
                                                },
                                                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                                    borderColor: errors.email ? '#d32f2f' : '#1976d2',
                                                },
                                            },
                                        }}
                                        required
                                    />
                                    {errors.email && (
                                        <SoftTypography
                                            variant="caption"
                                            color="error"
                                            sx={{mt: 0.5, ml: 1}}
                                        >
                                            {errors.email}
                                        </SoftTypography>
                                    )}
                                </SoftBox>
                            </Grid>

                            <Grid item xs={12}>
                                <SoftBox mb={0}>
                                    <SoftBox mb={1} ml={0.5}>
                                        <SoftTypography
                                            component="label"
                                            variant="caption"
                                            fontWeight="bold"
                                        >
                                            Phone Number *
                                        </SoftTypography>
                                    </SoftBox>
                                    <SoftBox sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <SoftTypography
                                            sx={{
                                                fontSize: '1rem',
                                                fontWeight: 400,
                                                color: 'rgba(0, 0, 0, 0.6)',
                                                lineHeight: 1.4375,
                                                minWidth: '20px'
                                            }}
                                        >
                                            +
                                        </SoftTypography>
                                        <SoftInput
                                            name="phoneNumber"
                                            type="tel"
                                            placeholder="60123456789"
                                            value={formData.phoneNumber}
                                            onChange={(e) => {
                                                // Only allow digits, strip any non-digit characters
                                                let value = e.target.value.replace(/\D/g, '');
                                                setFormData(prev => ({
                                                    ...prev,
                                                    phoneNumber: value
                                                }));
                                            }}
                                            error={!!errors.phoneNumber}
                                            sx={{
                                                flex: 1,
                                                '& .MuiOutlinedInput-root': {
                                                    '&.Mui-error .MuiOutlinedInput-notchedOutline': {
                                                        borderColor: '#d32f2f',
                                                    },
                                                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                                        borderColor: errors.phoneNumber ? '#d32f2f' : '#1976d2',
                                                    },
                                                },
                                            }}
                                            required
                                        />
                                    </SoftBox>
                                    {errors.phoneNumber && (
                                        <SoftTypography
                                            variant="caption"
                                            color="error"
                                            sx={{mt: 0.5, ml: 1}}
                                        >
                                            {errors.phoneNumber}
                                        </SoftTypography>
                                    )}
                                </SoftBox>
                            </Grid>

                            <Grid item xs={12} sm={6}>
                                <SoftBox mb={1} ml={0.5}>
                                    <SoftTypography
                                        component="label"
                                        variant="caption"
                                        fontWeight="bold"
                                    >
                                        User Role
                                    </SoftTypography>
                                </SoftBox>
                                <Select
                                    name="userRole"
                                    value={formData.userRole}
                                    onChange={handleChange}
                                    label="User Role"
                                    disabled={true}
                                    fullWidth
                                >
                                    <MenuItem value="GUARDIAN">Guardian</MenuItem>
                                </Select>
                            </Grid>

                            <Grid item xs={12} sm={6}>
                                <SoftBox mb={1} ml={0.5}>
                                    <SoftTypography
                                        component="label"
                                        variant="caption"
                                        fontWeight="bold"
                                    >
                                        Gender
                                    </SoftTypography>
                                </SoftBox>
                                <Select
                                    name="gender"
                                    value={formData.gender || ''}
                                    onChange={handleChange}
                                    displayEmpty
                                    fullWidth
                                    error={!!errors.gender}
                                    sx={{
                                        '& .MuiSelect-select': {
                                            padding: '10px 14px',
                                        },
                                        '& .MuiOutlinedInput-notchedOutline': {
                                            borderColor: errors.gender ? '#d32f2f' : '#d0d7de',
                                        },
                                        '&:hover .MuiOutlinedInput-notchedOutline': {
                                            borderColor: errors.gender ? '#d32f2f' : '#1976d2',
                                        },
                                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                            borderColor: errors.gender ? '#d32f2f' : '#1976d2',
                                        },
                                    }}
                                >
                                    <MenuItem value="" disabled>
                                        <em>Please select gender</em>
                                    </MenuItem>
                                    <MenuItem value="MALE">Male</MenuItem>
                                    <MenuItem value="FEMALE">Female</MenuItem>
                                    <MenuItem value="OTHER">Other</MenuItem>
                                </Select>
                                {errors.gender && (
                                    <SoftTypography
                                        variant="caption"
                                        color="error"
                                        sx={{mt: 0.5, ml: 1}}
                                    >
                                        {errors.gender}
                                    </SoftTypography>
                                )}
                            </Grid>

                            <Grid item xs={12}>
                                <Typography variant="h6" gutterBottom>
                                    Branch Assignment
                                </Typography>
                            </Grid>

                            <Grid item xs={12}>
                                <SoftBox mb={1} ml={0.5}>
                                    <SoftTypography
                                        component="label"
                                        variant="caption"
                                        fontWeight="bold"
                                    >
                                        Branch
                                    </SoftTypography>
                                </SoftBox>
                                <Select
                                    name="branchId"
                                    value={formData.branchId || ''}
                                    onChange={handleChange}
                                    displayEmpty
                                    fullWidth
                                    disabled={loadingBranches}
                                    sx={{
                                        '& .MuiSelect-select': {
                                            padding: '10px 14px',
                                        },
                                        '& .MuiOutlinedInput-notchedOutline': {
                                            borderColor: errors.branchId ? '#d32f2f' : '#d0d7de',
                                        },
                                        '&:hover .MuiOutlinedInput-notchedOutline': {
                                            borderColor: errors.branchId ? '#d32f2f' : '#1976d2',
                                        },
                                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                            borderColor: errors.branchId ? '#d32f2f' : '#1976d2',
                                        },
                                    }}
                                >
                                    {loadingBranches ? (
                                        <MenuItem disabled>
                                            <Box display="flex" alignItems="center" gap={1}>
                                                <CircularProgress size={16}/>
                                                Loading branches...
                                            </Box>
                                        </MenuItem>
                                    ) : branchesError ? (
                                        <MenuItem disabled>
                                            <Typography color="error">
                                                Error loading branches: {branchesError}
                                            </Typography>
                                        </MenuItem>
                                    ) : branches.length === 0 ? (
                                        <MenuItem disabled>
                                            No branches available
                                        </MenuItem>
                                    ) : (
                                        [
                                            <MenuItem key="default" value="" disabled>
                                                <em>Please select a branch</em>
                                            </MenuItem>,
                                            ...branches.map((branch) => (
                                                <MenuItem key={branch.branchId} value={branch.branchId}>
                                                    {branch.location}
                                                </MenuItem>
                                            ))
                                        ]
                                    )}
                                </Select>
                            </Grid>

                            <Grid item xs={12}>
                                <Typography variant="h6" gutterBottom>
                                    ID Information
                                </Typography>
                            </Grid>

                            <Grid item xs={12} sm={6}>
                                <SoftBox mb={1} ml={0.5}>
                                    <SoftTypography
                                        component="label"
                                        variant="caption"
                                        fontWeight="bold"
                                    >
                                        ID Type
                                    </SoftTypography>
                                </SoftBox>
                                <Select
                                    name="idType"
                                    value={formData.idType || ''}
                                    onChange={handleIdChange}
                                    displayEmpty
                                    fullWidth
                                    sx={{
                                        '& .MuiSelect-select': {
                                            padding: '10px 14px',
                                        },
                                        '& .MuiOutlinedInput-notchedOutline': {
                                            borderColor: '#d0d7de',
                                        },
                                        '&:hover .MuiOutlinedInput-notchedOutline': {
                                            borderColor: '#1976d2',
                                        },
                                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                            borderColor: '#1976d2',
                                        },
                                    }}
                                >
                                    <MenuItem value="" disabled>
                                        <em>Please select ID type</em>
                                    </MenuItem>
                                    <MenuItem value="MYKAD">MyKad</MenuItem>
                                    <MenuItem value="PASSPORT">Passport</MenuItem>
                                </Select>
                            </Grid>

                            <Grid item xs={12} sm={6}>
                                <SoftBox mb={0}>
                                    <SoftBox mb={1} ml={0.5}>
                                        <SoftTypography
                                            component="label"
                                            variant="caption"
                                            fontWeight="bold"
                                        >
                                            ID Number
                                        </SoftTypography>
                                    </SoftBox>
                                    <SoftInput
                                        name="idNumber"
                                        type="text"
                                        placeholder={formData.idType === 'MYKAD' ? 'Enter MyKad number (e.g., 901231-01-1234)' : 'Enter passport number'}
                                        value={formData.idNumber || ''}
                                        onChange={(e) => {
                                            // Format MyKad as user types
                                            if (formData.idType === 'MYKAD') {
                                                const formatted = formatMyKad(e.target.value);
                                                setFormData(prev => ({
                                                    ...prev,
                                                    idNumber: formatted
                                                }));

                                                // Auto-extract date of birth
                                                const newBirthDate = extractDOBFromMyKad(formatted);
                                                if (newBirthDate) {
                                                    setFormData(prev => ({
                                                        ...prev,
                                                        dob: new Date(newBirthDate).toISOString().split("T")[0]
                                                    }));
                                                }
                                            } else {
                                                handleIdChange(e);
                                            }
                                        }}
                                        disabled={!formData.idType}
                                        error={!!errors.idNumber}
                                        sx={{
                                            '& .MuiOutlinedInput-root': {
                                                '&.Mui-error .MuiOutlinedInput-notchedOutline': {
                                                    borderColor: '#d32f2f',
                                                },
                                                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                                    borderColor: errors.idNumber ? '#d32f2f' : '#1976d2',
                                                },
                                            },
                                        }}
                                    />
                                    {errors.idNumber && (
                                        <SoftTypography
                                            variant="caption"
                                            color="error"
                                            sx={{mt: 0.5, ml: 1}}
                                        >
                                            {errors.idNumber}
                                        </SoftTypography>
                                    )}
                                    {formData.idType === 'MYKAD' && !errors.idNumber && (
                                        <SoftTypography
                                            variant="caption"
                                            color="text.secondary"
                                            sx={{mt: 0.5, ml: 1}}
                                        >
                                            Format: YYMMDDPBXXXX
                                        </SoftTypography>
                                    )}
                                </SoftBox>
                            </Grid>

                            <Grid item xs={12} sm={6}>
                                <SoftBox mb={0}>
                                    <SoftBox mb={1} ml={0.5}>
                                        <SoftTypography
                                            component="label"
                                            variant="caption"
                                            fontWeight="bold"
                                        >
                                            Date of Birth
                                        </SoftTypography>
                                    </SoftBox>
                                    <SoftInput
                                        name="dob"
                                        type="date"
                                        placeholder="yyyy-mm-dd"
                                        value={formData.dob || ''}
                                        onChange={handleChange}
                                        disabled={formData.idType === 'MYKAD' && formData.idNumber}
                                        error={!!errors.dob}
                                        sx={{
                                            '& .MuiOutlinedInput-root': {
                                                '&.Mui-error .MuiOutlinedInput-notchedOutline': {
                                                    borderColor: '#d32f2f',
                                                },
                                                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                                    borderColor: errors.dob ? '#d32f2f' : '#1976d2',
                                                },
                                            },
                                        }}
                                    />
                                    {errors.dob && (
                                        <SoftTypography
                                            variant="caption"
                                            color="error"
                                            sx={{mt: 0.5, ml: 1}}
                                        >
                                            {errors.dob}
                                        </SoftTypography>
                                    )}
                                    {formData.idType === 'MYKAD' && formData.idNumber && !errors.dob && (
                                        <SoftTypography
                                            variant="caption"
                                            color="text.secondary"
                                            sx={{mt: 0.5, ml: 1}}
                                        >
                                            Auto-extracted from MyKad
                                        </SoftTypography>
                                    )}
                                </SoftBox>
                            </Grid>

                            <Grid item xs={12} sm={6}>
                                <SoftBox mb={1} ml={0.5}>
                                    <SoftTypography
                                        component="label"
                                        variant="caption"
                                        fontWeight="bold"
                                    >
                                        Status
                                    </SoftTypography>
                                </SoftBox>
                                <Select
                                    name="status"
                                    value={formData.status || ''}
                                    onChange={handleChange}
                                    displayEmpty
                                    fullWidth
                                    sx={{
                                        '& .MuiSelect-select': {
                                            padding: '10px 14px',
                                        },
                                        '& .MuiOutlinedInput-notchedOutline': {
                                            borderColor: '#d0d7de',
                                        },
                                        '&:hover .MuiOutlinedInput-notchedOutline': {
                                            borderColor: '#1976d2',
                                        },
                                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                            borderColor: '#1976d2',
                                        },
                                    }}
                                >
                                    <MenuItem value="" disabled>
                                        <em>Please select status</em>
                                    </MenuItem>
                                    <MenuItem value="ACTIVE">Active</MenuItem>
                                    <MenuItem value="INACTIVE">Inactive</MenuItem>
                                    <MenuItem value="IDLE">Idle</MenuItem>
                                </Select>
                            </Grid>
                        </Grid>
                        {error && (
                            <div style={{color: "red", marginTop: "10px"}}>
                                {error}
                            </div>
                        )}
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={onClose}>Cancel</Button>
                        <SoftButton
                            type="submit"
                            variant="contained"
                            color="primary"
                            disable={error || loadingBranches}
                        >
                            {isEdit ? "Update Guardian" : "Add Guardian"}
                        </SoftButton>
                    </DialogActions>
                </form>
            </Dialog>
        </>
    );
}

AddGuardianDialog.propTypes = {
    open: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    onSubmit: PropTypes.func.isRequired,
    guardian: PropTypes.object,
    isEdit: PropTypes.bool
};

export default AddGuardianDialog;
